// --- pages/Dashboard.jsx ---
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HorseCard from '../../components/horseDetails/HorseCard';
import api from '../../utils/api';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('forSale');
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [stats, setStats] = useState({ bought: 0, sold: 0, totalSpent: 0, totalMade: 0 });
  const hasFetched = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      localStorage.removeItem('horseList');
      navigate('/login');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const fetchHorses = async () => {
      try {
        const cached = localStorage.getItem('horseList');
        if (cached) {
          const parsed = JSON.parse(cached);
          setHorses(parsed);
          console.log('📦 Horses loaded from cache:', parsed);
          computeStats(parsed);
          return;
        }

        const res = await api.get('/horses/myhorses');
        console.log('✅ Horses fetched:', res.data);
        setHorses(res.data);
        localStorage.setItem('horseList', JSON.stringify(res.data));
        computeStats(res.data);
      } catch (err) {
        console.error('❌ Failed to fetch horses:', err);
      }
    };

    if (user && !loading && !hasFetched.current) {
      hasFetched.current = true;
      fetchHorses();
    }

    // Clear horse cache on logout
    if (!user && !loading) {
      localStorage.removeItem('horseList');
    }
  }, [user, loading]);

  const computeStats = (horseArray) => {
    const sold = horseArray.filter(h => h.status === 'Sold');
    const bought = horseArray.filter(h => h.status === 'Bought');
    const totalSpent = bought.reduce((acc, h) => acc + (h.saleInfo?.buyNowPrice || 0), 0);
    const totalMade = sold.reduce((acc, h) => acc + (h.saleInfo?.buyNowPrice || 0), 0);

    setStats({
      bought: bought.length,
      sold: sold.length,
      totalSpent: `$${totalSpent.toLocaleString()}`,
      totalMade: `$${totalMade.toLocaleString()}`,
    });
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const mapTabToStatus = (tab) => {
    switch (tab) {
      case 'forSale': return 'For Sale';
      case 'sold': return 'Sold';
      case 'pending': return 'Pending';
      case 'bids': return 'Active Bid';
      case 'bought': return 'Bought';
      default: return 'For Sale';
    }
  };

  const filteredHorses = horses.filter(horse => horse.status === mapTabToStatus(activeTab));
  const totalPages = Math.ceil(filteredHorses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedHorses = filteredHorses.slice(startIndex, startIndex + itemsPerPage);

  if (loading) return <div className="text-center py-10 text-lg">🐎 Loading your dashboard...</div>;
  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto p-4">
      {/* Welcome Section */}
      <div className="grid grid-cols-4 my-8 px-4 items-center">
        <div className="col-span-3 font-montserrat text-4xl text-primary">
          <h1 className="justify-self-center text-5xl text-primary font-medium font-cormorant">Welcome</h1>
          <h1 className="justify-self-center text-5xl text-primary font-medium font-cormorant">{user.firstName || user.displayName}</h1>
        </div>
        <img src={user.profileImage} alt="profile image" className="w-30 h-30 object-cover rounded-full" />
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-8">
        <StatCard label="Horses Bought" value={stats.bought} />
        <StatCard label="Horses Sold" value={stats.sold} />
        <StatCard label="Total Spent" value={stats.totalSpent} />
        <StatCard label="Total Made" value={stats.totalMade} />
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto whitespace-nowrap my-8 scroll-hide">
        <div className="flex gap-2">
          {['forSale', 'sold', 'pending', 'bids', 'bought'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-mentserrat rounded-full text-sm ${activeTab === tab ? 'bg-primary text-white' : 'border-2 border-solid border-clay text-clay'}`}
            >
              {tab.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Horse Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {paginatedHorses.map((horse) => (
          <HorseCard key={horse._id} {...horse} fromTab={activeTab} />
        ))}
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-6 flex-wrap gap-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          className="px-3 py-1 border-2 rounded border-primary disabled:opacity-50"
        >
          Prev
        </button>
        {[...Array(totalPages)].map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentPage(i + 1)}
            className={`px-3 py-1 ${currentPage === i + 1 ? 'bg-primary rounded text-white' : 'border-b-2 border-primary'}`}
          >
            {i + 1}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          className="px-3 py-1 border-2 rounded border-primary disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-white p-4 drop-shadow-md shadow-primary rounded text-center font-montserrat">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}